import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import TokenUploadCropModal from "./TokenUploadCropModal";
import SoundPicker from "./SoundPicker";
import ChestInspector from "./ChestInspector";
import TokenAnimationMakerModal from "./TokenAnimationMakerModal";
import { toast } from "sonner";
import { playSfxAsset, triggerSound } from "../../lib/soundBus";
import { X, Trash2, ImagePlus, Feather, Copy, Swords, Volume2, Wand2 } from "lucide-react";

const ATTACK_CLIP_LABELS = { attack: "Generic", attack_melee: "Melee", attack_magic: "Magic", attack_bow: "Bow", attack_throwing: "Throwing" };

export default function TokenInspector({ gameId, isGm, myMemberId, members, game, scene, character, stats, onOpenSheet }) {
  const selectedTokenId = useGameStore((s) => s.selectedTokenId);
  const setSelectedTokenId = useGameStore((s) => s.setSelectedTokenId);
  const tokens = useGameStore((s) => s.tokens);
  const removeTokenLocal = useGameStore((s) => s.removeToken);
  const token = tokens.find((t) => t.id === selectedTokenId);
  const [name, setName] = useState("");
  const [conditions, setConditions] = useState([]);
  const [swapOpen, setSwapOpen] = useState(false);
  const [attackTargets, setAttackTargets] = useState({});
  const [attackRolling, setAttackRolling] = useState({});
  const [attackResults, setAttackResults] = useState({});
  const [draft, setDraft] = useState({});
  const [newAttack, setNewAttack] = useState({ name: "", bonus: "", damage: "", range_ft: "" });
  const [gangCreating, setGangCreating] = useState(false);
  const [gangNewName, setGangNewName] = useState("");
  const [giveLightTarget, setGiveLightTarget] = useState("");
  const [attackSfxOpen, setAttackSfxOpen] = useState(new Set());
  const [animMakerOpen, setAnimMakerOpen] = useState(false);
  const [hasGmPass, setHasGmPass] = useState(false);
  const [myAnimations, setMyAnimations] = useState([]);
  const [chosenAttackClip, setChosenAttackClip] = useState(null);

  useEffect(() => {
    api.get("/pf2e/conditions").then((r) => setConditions(r.data));
  }, []);

  const refreshMyAnimations = () => api.get("/animations/mine").then((r) => setMyAnimations(r.data)).catch(() => {});

  useEffect(() => {
    if (!isGm) return;
    api.get("/billing/me").then((r) => setHasGmPass(!!r.data?.has_gm_entitlement)).catch(() => {});
    refreshMyAnimations();
  }, [isGm]);

  useEffect(() => setName(token?.name || ""), [token?.id]);

  // Local draft buffer for every input the GM can type/edit — prevents the "typing on top of
  // itself" glitch you'd get from patching the server on every keystroke and waiting for the
  // websocket echo to come back before the field's value updates. Only re-syncs from the live
  // token when a NEW token is selected; edits commit to the server on blur.
  useEffect(() => {
    if (!token) return;
    setDraft({
      hp_current: token.hp_current, hp_max: token.hp_max, speed_ft: token.speed_ft,
      light_radius_ft: token.light_radius_ft, light_type: token.light_type, altitude_ft: token.altitude_ft,
      dialogue_text: (token.dialogue_lines || []).join("\n"), perception_dc: token.perception_dc,
      sight_range_ft: token.sight_range_ft, sight_angle_deg: token.sight_angle_deg,
      sight_direction_deg: token.sight_direction_deg, sight_dc_far: token.sight_dc_far, sight_dc_near: token.sight_dc_near,
      sight_pivot_arc_deg: token.sight_pivot_arc_deg, sight_pivot_period_sec: token.sight_pivot_period_sec,
      sb_ac: token.stat_block?.ac, sb_speed: token.stat_block?.speed, sb_perception: token.stat_block?.perception,
      sb_fort: token.stat_block?.fort, sb_ref: token.stat_block?.ref, sb_will: token.stat_block?.will,
    });
  }, [token?.id]);

  // Drop/Give-light mutate light_radius_ft/light_type on the SAME token (no id change), which the
  // above id-scoped effect won't catch — re-sync just those two fields whenever they change so the
  // input doesn't keep showing a stale pre-drop/give value.
  useEffect(() => {
    if (!token) return;
    setDraft((d) => ({ ...d, light_radius_ft: token.light_radius_ft, light_type: token.light_type }));
  }, [token?.light_radius_ft, token?.light_type]);

  if (!token) return null;

  const attackAnimKeys = Object.keys(token.animation_clips || {}).filter((k) => k === "attack" || k.startsWith("attack_"));
  const effectiveAttackClip = attackAnimKeys.includes(chosenAttackClip) ? chosenAttackClip : attackAnimKeys[0] || null;

  const setDraftField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const commit = (key, patchKey, transform) => patch({ [patchKey || key]: transform ? transform(draft[key]) : draft[key] });
  const toNum = (v) => (v === "" || v == null ? null : Number(v));
  const toInt = (fallback) => (v) => (v === "" || v == null ? fallback : parseInt(v, 10) || fallback);

  const addStatBlock = () => patch({ stat_block: { ac: 10, speed: 25, perception: 0, fort: 0, ref: 0, will: 0, attacks: [], abilities: "" } });

  const addAttack = () => {
    if (!newAttack.name.trim()) return toast.error("Give the attack a name");
    const attacks = [...(token.stat_block?.attacks || []), {
      name: newAttack.name.trim(), bonus: parseInt(newAttack.bonus, 10) || 0, damage: newAttack.damage.trim() || "1d4",
      range_ft: newAttack.range_ft === "" ? 5 : parseInt(newAttack.range_ft, 10) || 5,
    }];
    patch({ stat_block: { ...token.stat_block, attacks } });
    setNewAttack({ name: "", bonus: "", damage: "", range_ft: "" });
  };

  const removeAttack = (idx) => {
    const attacks = (token.stat_block?.attacks || []).filter((_, i) => i !== idx);
    patch({ stat_block: { ...token.stat_block, attacks } });
  };

  const setAttackSfx = (idx, field, assetId) => {
    const attacks = (token.stat_block?.attacks || []).map((a, i) => (i === idx ? { ...a, [field]: assetId } : a));
    patch({ stat_block: { ...token.stat_block, attacks } });
  };

  const toggleAttackSfxOpen = (idx) => {
    setAttackSfxOpen((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const commitStat = (key, statKey) => patch({ stat_block: { ...token.stat_block, [statKey]: toInt(0)(draft[key]) } });

  const removeToken = async () => {
    await api.delete(`/games/${gameId}/tokens/${token.id}`);
    removeTokenLocal(token.id);
    setSelectedTokenId(null);
  };

  const quickAbilityRoll = async (ability, modifier) => {
    if (!character?.id) return toast.error("Create a character first to roll");
    try {
      await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label: `${ability.toUpperCase()} Check`, modifier });
    } catch {
      toast.error("Roll failed");
    }
  };

  if (token.token_type === "chest") {
    return (
      <>
        <ChestInspector
          gameId={gameId}
          token={token}
          onClose={() => setSelectedTokenId(null)}
          onDelete={removeToken}
          onSwapImage={() => setSwapOpen(true)}
        />
        <TokenUploadCropModal
          open={swapOpen}
          onOpenChange={setSwapOpen}
          gameId={gameId}
          onUploaded={({ url }) => api.patch(`/games/${gameId}/tokens/${token.id}`, { image_url: url })}
        />
      </>
    );
  }

  const patch = async (updates) => {
    await api.patch(`/games/${gameId}/tokens/${token.id}`, updates);
  };

  const applyExistingAnimation = async (animId) => {
    try {
      await api.post(`/games/${gameId}/tokens/${token.id}/apply-animation`, { animation_id: animId });
      toast.success("Animation applied");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply animation");
    }
  };

  const clearTokenAnimation = async () => {
    await api.post(`/games/${gameId}/tokens/${token.id}/apply-animation`, { animation_id: null });
    toast.success("Animation removed — back to the static image");
  };

  const tokenConditions = token.condition_ids || [];
  const toggleCondition = (slug) => {
    const exists = tokenConditions.some((c) => c.slug === slug);
    const updated = exists ? tokenConditions.filter((c) => c.slug !== slug) : [...tokenConditions, { slug, value: 1 }];
    patch({ condition_ids: updated });
  };

  const duplicateToken = async () => {
    try {
      const { data } = await api.post(`/games/${gameId}/tokens/${token.id}/duplicate`);
      toast.success(`Duplicated as "${data.name}" — edit its name/stock independently`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not duplicate token");
    }
  };

  const dropLight = async () => {
    try {
      await api.post(`/games/${gameId}/tokens/${token.id}/drop-light`);
      toast.success("Light dropped on the ground — anyone can pick it up");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not drop the light");
    }
  };

  const giveLight = async () => {
    if (!giveLightTarget) return toast.error("Pick a party member first");
    try {
      await api.post(`/games/${gameId}/tokens/${token.id}/give-light`, { target_member_id: giveLightTarget });
      toast.success("Light handed off");
      setGiveLightTarget("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not hand off the light");
    }
  };

  // GM-only: let a monster's stat-block attack actually roll against a chosen player token,
  // reusing the same attack-roll/damage-roll endpoints players use for parity — including
  // on-hit effects (poison, conditions, etc.) applying to the target the same way.
  const pcTargets = tokens.filter((t) => t.id !== token.id && t.token_type !== "npc" && t.token_type !== "chest" && t.token_type !== "light");
  const tokenScene = (game?.scenes || []).find((s) => s.id === token.scene_id);
  const pxPerFt = (tokenScene?.grid_size || 50) / 5;
  const distanceToFt = (target) => Math.hypot(token.x - target.x, token.y - target.y) / pxPerFt;

  const getTargetAc = async (targetToken) => {
    const member = (members || []).find((m) => m.id === targetToken.created_by);
    if (!member?.character_id) return targetToken.stat_block?.ac ?? 10;
    try {
      if (game?.system === "dnd5e") {
        const { data } = await api.get(`/characters/${member.character_id}`);
        return data.dnd5e?.ac ?? 10;
      }
      const { data } = await api.get(`/characters/${member.character_id}/stats`);
      return data.ac ?? 10;
    } catch {
      return 10;
    }
  };

  const rollNpcAttack = async (attack, idx) => {
    const target = pcTargets.find((t) => t.id === attackTargets[idx]);
    if (!target) return toast.error("Pick a target first");
    const rangeFt = attack.range_ft || 5;
    const distFt = distanceToFt(target);
    if (distFt > rangeFt) {
      return toast.error(`${target.name} is ${Math.round(distFt)}ft away — ${attack.name || "this attack"} only reaches ${rangeFt}ft`);
    }
    if (token.attack_sfx_asset_id) playSfxAsset(token.attack_sfx_asset_id);
    if (token.is_animated && effectiveAttackClip) {
      api.post(`/games/${gameId}/tokens/${token.id}/play-animation`, { clip: effectiveAttackClip }).catch(() => {});
    }
    setAttackRolling((r) => ({ ...r, [idx]: true }));
    try {
      const targetAc = await getTargetAc(target);
      const label = `${token.name} — ${attack.name || "Attack"}`;
      const { data } = await api.post(`/characters/npc/attack-roll`, {
        game_id: gameId, attacker_token_id: token.id, target_token_id: target.id,
        label, attack_bonus: attack.bonus || 0, target_ac: targetAc,
      });
      const isHit = data.outcome === "hit" || data.outcome === "critical_hit";
      triggerSound(gameId, { tag: isHit ? "attack_hit" : "attack_miss", overrideAssetId: isHit ? attack.hit_sfx_id : attack.miss_sfx_id });
      let damage = null;
      if (data.outcome === "hit" || data.outcome === "critical_hit") {
        const dmgRes = await api.post(`/characters/npc/damage-roll`, {
          game_id: gameId, label, dice: attack.damage || "1d4", bonus: 0,
          crit: data.outcome === "critical_hit", apply_to_token_id: target.id, on_hit_effect: attack.on_hit_effect || null,
        });
        damage = dmgRes.data;
      }
      setAttackResults((r) => ({ ...r, [idx]: { ...data, damage } }));
    } catch {
      toast.error("Attack roll failed");
    } finally {
      setAttackRolling((r) => ({ ...r, [idx]: false }));
    }
  };

  return (
    <div className="absolute bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-20 w-[92vw] max-w-md max-h-[70vh] overflow-y-auto scrollbar-thin bg-black/70 backdrop-blur-2xl border border-white/10 rounded-2xl p-4 space-y-3" data-testid="token-inspector">
      <div className="flex items-center justify-between">
        <Input
          data-testid="token-inspector-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => patch({ name })}
          className="bg-black/30 border-white/10 h-8 text-sm"
        />
        <button data-testid="token-inspector-close" onClick={() => setSelectedTokenId(null)} className="ml-2 text-[#f1e0c5]/40 hover:text-[#D4AF37]">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!isGm && token.created_by === myMemberId && token.token_type === "pc" && character && (
        <div className="space-y-2 bg-black/20 border border-white/10 rounded-lg p-2.5" data-testid="token-inspector-player-panel">
          {stats?.ability_mods && (
            <div>
              <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Quick Rolls</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(stats.ability_mods).map(([ability, mod]) => (
                  <button
                    key={ability}
                    data-testid={`token-inspector-quick-roll-${ability}`}
                    onClick={() => quickAbilityRoll(ability, mod)}
                    className="px-2.5 py-1 rounded-full bg-black/30 border border-white/10 text-[10px] uppercase tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
                  >
                    {ability} {mod >= 0 ? "+" : ""}{mod}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Button data-testid="token-inspector-view-sheet" size="sm" variant="secondary" className="w-full rounded-full text-xs" onClick={onOpenSheet}>
            View Character Sheet
          </Button>
          {game?.system === "pf2e" && (
            <>
              <label className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-2.5 py-1.5 cursor-pointer">
                <span className="text-xs text-[#f1e0c5]/70">Auto-Attack on my turn</span>
                <Checkbox data-testid="token-inspector-auto-attack-toggle" checked={!!token.auto_attack} onCheckedChange={(v) => patch({ auto_attack: !!v })} />
              </label>
              <p className="text-[9px] text-[#f1e0c5]/30 -mt-1">When it's your turn in combat, you'll auto-attack the nearest enemy with a drawn weapon instead of needing to open the Attack menu.</p>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-14">Size</span>
        <Slider data-testid="token-inspector-size-slider" value={[token.size]} min={20} max={150} step={5} onValueChange={([v]) => patch({ size: v })} />
        {tokenScene?.grid_size && token.size !== tokenScene.grid_size && (
          <button
            data-testid="token-inspector-reset-size-to-grid"
            onClick={() => patch({ size: tokenScene.grid_size })}
            title="Reset to 1 grid square — fixes a token left tiny/huge after the map's grid size changed"
            className="shrink-0 text-[9px] uppercase tracking-wide text-[#D4AF37]/70 hover:text-[#D4AF37] border border-[#D4AF37]/30 hover:border-[#D4AF37] rounded-full px-2 py-1 whitespace-nowrap"
          >
            Reset to Grid
          </button>
        )}
      </div>

      <button
        data-testid="token-inspector-lock-toggle"
        onClick={() => patch({ locked: !token.locked })}
        className={`w-full flex items-center justify-center gap-2 py-1.5 rounded-full border text-xs uppercase tracking-widest transition-colors duration-200 ${
          token.locked ? "bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]" : "bg-black/30 border-white/10 text-[#f1e0c5]/40 hover:border-[#D4AF37]/40"
        }`}
      >
        {token.locked ? "Locked in place — click to unlock" : "Unlocked — click to lock in place"}
      </button>

      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-14">HP</span>
        <Input
          data-testid="token-inspector-hp-input"
          type="number"
          value={draft.hp_current ?? ""}
          placeholder="Current"
          onChange={(e) => setDraftField("hp_current", e.target.value)}
          onBlur={() => commit("hp_current", "hp_current", toInt(0))}
          className="bg-black/30 border-white/10 h-7 w-24 px-2 text-xs"
        />
        <Input
          data-testid="token-inspector-hp-max-input"
          type="number"
          value={draft.hp_max ?? ""}
          placeholder="Max"
          onChange={(e) => setDraftField("hp_max", e.target.value)}
          onBlur={() => commit("hp_max", "hp_max", toInt(0))}
          className="bg-black/30 border-white/10 h-7 w-24 px-2 text-xs"
        />
      </div>

      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-14">Speed</span>
        <Input
          data-testid="token-inspector-speed-input"
          type="number"
          value={draft.speed_ft ?? 25}
          onChange={(e) => setDraftField("speed_ft", e.target.value)}
          onBlur={() => commit("speed_ft", "speed_ft", toInt(0))}
          className="bg-black/30 border-white/10 h-7 w-20 text-xs"
        />
        <span className="text-[10px] text-[#f1e0c5]/30">ft per move (used when Speed Restrictions is on)</span>
      </div>

      {(isGm || token.created_by === myMemberId) && (
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-14">Vision</span>
          <Select value={token.vision_type || "normal"} onValueChange={(v) => patch({ vision_type: v })}>
            <SelectTrigger data-testid="token-inspector-vision-select" className="bg-black/30 border-white/10 h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low-light-vision">Low-Light Vision</SelectItem>
              <SelectItem value="darkvision">Darkvision</SelectItem>
              <SelectItem value="see-in-darkness">See in Darkness (pierces magical)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {(isGm || token.created_by === myMemberId) && (
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-14">Light</span>
          <Input
            data-testid="token-inspector-light-radius-input"
            type="number"
            min={0}
            value={draft.light_radius_ft ?? 0}
            onChange={(e) => setDraftField("light_radius_ft", e.target.value)}
            onBlur={() => commit("light_radius_ft", "light_radius_ft", (v) => Math.max(0, toInt(0)(v)))}
            className="bg-black/30 border-white/10 h-7 w-16 text-xs"
          />
          <Select value={draft.light_type || "fire"} onValueChange={(v) => { setDraftField("light_type", v); patch({ light_type: v }); }}>
            <SelectTrigger data-testid="token-inspector-light-type-select" className="bg-black/30 border-white/10 h-7 text-xs w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fire">Fire</SelectItem>
              <SelectItem value="magic">Magic</SelectItem>
              <SelectItem value="electric">Electric</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[10px] text-[#f1e0c5]/30">ft radius — cuts through darkness/FOW for everyone. 0 = no light</span>
        </div>
      )}

      {isGm && token.token_type === "light" && (
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox data-testid="token-inspector-light-pickable-toggle" checked={!!token.light_pickable} onCheckedChange={(v) => patch({ light_pickable: !!v })} />
          <span className="text-[10px] text-[#f1e0c5]/50">Players can pick this up and attach it to their token</span>
        </label>
      )}

      {token.token_type !== "light" && (token.light_radius_ft || 0) > 0 && (isGm || token.created_by === myMemberId) && (
        <div className="space-y-1.5 bg-black/20 border border-white/10 rounded-xl p-2.5">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Carried Light</p>
          <div className="flex gap-1.5">
            <Button data-testid="token-inspector-drop-light-button" size="sm" variant="secondary" className="rounded-full text-xs h-7 flex-1" onClick={dropLight}>Drop</Button>
          </div>
          {(members || []).filter((m) => m.role !== "gm" && m.id !== token.created_by).length > 0 && (
            <div className="flex gap-1.5">
              <Select value={giveLightTarget || "none"} onValueChange={setGiveLightTarget}>
                <SelectTrigger data-testid="token-inspector-give-light-select" className="bg-black/30 border-white/10 h-7 text-xs flex-1"><SelectValue placeholder="Give to..." /></SelectTrigger>
                <SelectContent>
                  {(members || []).filter((m) => m.role !== "gm" && m.id !== token.created_by).map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="token-inspector-give-light-button" size="sm" variant="secondary" className="rounded-full text-xs h-7 shrink-0" onClick={giveLight}>Give</Button>
            </div>
          )}
        </div>
      )}

      {token.created_by === myMemberId && (
        <label className="flex items-center gap-2 text-xs text-[#f1e0c5]/70 cursor-pointer">
          <input
            data-testid="token-inspector-use-video-checkbox"
            type="checkbox"
            checked={!!token.use_video_token}
            onChange={(e) => patch({ use_video_token: e.target.checked })}
            className="accent-[#D90429]"
          />
          Use my live camera as this token
        </label>
      )}

      {(isGm || token.created_by === myMemberId) && (
        <div className="flex items-center justify-between bg-black/20 border border-white/10 rounded-lg px-3 py-2">
          <div>
            <p className="text-xs text-[#f1e0c5]/80">Weapon {token.weapon_drawn === false ? "Stowed" : "Drawn"}</p>
            <p className="text-[9px] text-[#f1e0c5]/30">Stowing hides the reach ring and blocks Strikes until re-drawn (PF2e: 1 Interact action; 5e: your free object interaction).</p>
          </div>
          <button
            data-testid="token-inspector-weapon-drawn-toggle"
            onClick={async () => {
              const next = token.weapon_drawn === false;
              await patch({ weapon_drawn: next });
              await api.post(`/games/${gameId}/chat`, { content: `${token.name} ${next ? "draws" : "stows"} their weapon.` });
            }}
            className={`text-[10px] px-3 py-1 rounded-full border shrink-0 ${token.weapon_drawn === false ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#4d7c0f] text-[#4d7c0f]"}`}
          >
            {token.weapon_drawn === false ? "Draw" : "Stow"}
          </button>
        </div>
      )}

      {(isGm || token.created_by === myMemberId) && (
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-[#f1e0c5]/70 cursor-pointer w-20 shrink-0">
            <input
              data-testid="token-inspector-flying-checkbox"
              type="checkbox"
              checked={!!token.flying}
              onChange={(e) => patch({ flying: e.target.checked })}
              className="accent-[#D90429]"
            />
            <Feather className="w-3 h-3 text-[#D4AF37]" /> Flying
          </label>
          {token.flying && (
            <>
              <Input
                data-testid="token-inspector-altitude-input"
                type="number"
                min={0}
                value={draft.altitude_ft ?? 0}
                onChange={(e) => setDraftField("altitude_ft", e.target.value)}
                onBlur={() => commit("altitude_ft", "altitude_ft", (v) => Math.max(0, toInt(0)(v)))}
                className="bg-black/30 border-white/10 h-7 w-20 text-xs"
              />
              <span className="text-[10px] text-[#f1e0c5]/30">ft altitude</span>
            </>
          )}
        </div>
      )}

      {isGm && (
        <div>
          <SoundPicker
            gameId={gameId}
            value={token.sfx_asset_id}
            onChange={(id) => patch({ sfx_asset_id: id })}
            label="Turn Sound"
            testId="token-inspector-sound-picker"
          />
          <p className="text-[9px] text-[#f1e0c5]/30 pl-[60px]">Plays once when this token's turn begins in combat.</p>
        </div>
      )}

      {isGm && !token.is_approved && (
        <Button data-testid="token-inspector-approve" size="sm" className="w-full rounded-full bg-[#4d7c0f] hover:bg-[#3f6608]" onClick={() => patch({ is_approved: true })}>
          Approve Token
        </Button>
      )}

      {isGm && token.token_type === "npc" && (
        <div className="space-y-2 border-t border-white/10 pt-3">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">NPC Interaction</p>
          <textarea
            data-testid="token-inspector-dialogue-textarea"
            value={draft.dialogue_text ?? ""}
            onChange={(e) => setDraftField("dialogue_text", e.target.value)}
            onBlur={() => patch({ dialogue_lines: (draft.dialogue_text || "").split("\n") })}
            placeholder={"A line of dialogue this NPC can say (one per line)\ne.g. \"What'll it be, stranger?\""}
            rows={3}
            className="w-full bg-black/30 border border-white/10 rounded-lg p-2 text-xs resize-none"
          />
          <div>
            <SoundPicker
              gameId={gameId}
              value={token.greet_sfx_asset_id}
              onChange={(id) => patch({ greet_sfx_asset_id: id })}
              label="Greet Sound"
              testId="token-inspector-greet-sound-picker"
            />
            <p className="text-[9px] text-[#f1e0c5]/30 pl-[60px]">A quick voice flare when a player opens Talk with this NPC.</p>
          </div>
          <div>
            <SoundPicker
              gameId={gameId}
              value={token.attack_sfx_asset_id}
              onChange={(id) => patch({ attack_sfx_asset_id: id })}
              label="Attack Sound"
              testId="token-inspector-attack-sound-picker"
            />
            <p className="text-[9px] text-[#f1e0c5]/30 pl-[60px]">Plays when you roll one of this NPC's attacks below.</p>
          </div>
          <div>
            <SoundPicker
              gameId={gameId}
              value={token.death_sfx_asset_id}
              onChange={(id) => patch({ death_sfx_asset_id: id })}
              label="Death Sound"
              testId="token-inspector-death-sound-picker"
            />
            <p className="text-[9px] text-[#f1e0c5]/30 pl-[60px]">Plays for everyone when this NPC's HP hits 0.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#f1e0c5]/70 cursor-pointer">
            <input
              data-testid="token-inspector-allow-attack-checkbox"
              type="checkbox"
              checked={!!token.allow_attack}
              onChange={(e) => patch({ allow_attack: e.target.checked })}
              className="accent-[#D90429]"
            />
            Allow players to attack this NPC
          </label>
          <label className="flex items-center gap-2 text-xs text-[#f1e0c5]/70 cursor-pointer">
            <input
              data-testid="token-inspector-shopkeeper-checkbox"
              type="checkbox"
              checked={!!token.is_shopkeeper}
              onChange={(e) => patch({ is_shopkeeper: e.target.checked })}
              className="accent-[#4d7c0f]"
            />
            Mark as Shopkeeper (stock their shop from the Rewards tab)
          </label>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-24">Perception DC</span>
            <Input
              data-testid="token-inspector-perception-dc-input"
              type="number"
              value={draft.perception_dc ?? ""}
              placeholder="15 (default)"
              onChange={(e) => setDraftField("perception_dc", e.target.value)}
              onBlur={() => commit("perception_dc", "perception_dc", toNum)}
              className="bg-black/30 border-white/10 h-7 text-xs flex-1"
            />
          </div>
          <p className="text-[10px] text-[#f1e0c5]/30">Used to auto-resolve Pickpocket attempts against this NPC.</p>
          <div className="space-y-1.5">
            <span className="text-[10px] uppercase text-[#f1e0c5]/40">Gang</span>
            {gangCreating ? (
              <div className="flex gap-2">
                <Input
                  data-testid="token-inspector-gang-new-input"
                  value={gangNewName}
                  onChange={(e) => setGangNewName(e.target.value)}
                  placeholder="New gang name…"
                  className="bg-black/30 border-white/10 h-8 text-xs flex-1"
                />
                <Button
                  data-testid="token-inspector-gang-new-confirm"
                  size="sm"
                  className="h-8 rounded-full bg-[#4d7c0f] hover:bg-[#3f6608] text-xs"
                  disabled={!gangNewName.trim()}
                  onClick={() => { patch({ gang_name: gangNewName.trim() }); setGangCreating(false); setGangNewName(""); }}
                >
                  Add
                </Button>
                <button data-testid="token-inspector-gang-new-cancel" onClick={() => setGangCreating(false)} className="text-[#f1e0c5]/40 hover:text-white text-xs px-1">✕</button>
              </div>
            ) : (
              <Select
                value={token.gang_name || "__none__"}
                onValueChange={(v) => {
                  if (v === "__new__") setGangCreating(true);
                  else patch({ gang_name: v === "__none__" ? null : v });
                }}
              >
                <SelectTrigger data-testid="token-inspector-gang-select" className="bg-black/30 border-white/10 h-8 text-xs">
                  <SelectValue placeholder="No gang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No gang</SelectItem>
                  {[...new Set(tokens.filter((t) => t.token_type === "npc" && t.gang_name && t.id !== token.id).map((t) => t.gang_name))].map((g) => (
                    <SelectItem key={g} value={g} data-testid={`token-inspector-gang-option-${g}`}>{g}</SelectItem>
                  ))}
                  <SelectItem value="__new__" data-testid="token-inspector-gang-new-option">+ New gang…</SelectItem>
                </SelectContent>
              </Select>
            )}
            <p className="text-[9px] text-[#f1e0c5]/30">NPCs sharing a gang reveal each other + join the same fight the moment one is attacked or spots a player.</p>
          </div>
          <div className="space-y-1.5 pt-1">
            <span className="text-[10px] uppercase text-[#f1e0c5]/40">Combat AI</span>
            <label className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-2.5 py-1.5 cursor-pointer">
              <span className="text-xs text-[#f1e0c5]/70">Auto-Attack on its turn</span>
              <Checkbox data-testid="token-inspector-npc-auto-attack-toggle" checked={!!token.auto_attack} onCheckedChange={(v) => patch({ auto_attack: !!v })} />
            </label>
            <label className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-2.5 py-1.5 cursor-pointer">
              <span className="text-xs text-[#f1e0c5]/70">Can move while fighting</span>
              <Checkbox data-testid="token-inspector-npc-can-move-toggle" checked={!!token.can_move_while_fighting} onCheckedChange={(v) => patch({ can_move_while_fighting: !!v })} />
            </label>
            <label className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-2.5 py-1.5 cursor-pointer">
              <span className="text-xs text-[#f1e0c5]/70">Can chase (uses its speed)</span>
              <Checkbox data-testid="token-inspector-npc-can-chase-toggle" checked={!!token.can_chase} onCheckedChange={(v) => patch({ can_chase: !!v })} />
            </label>
            <p className="text-[9px] text-[#f1e0c5]/30">With Auto-Attack off, an "incoming attack" icon lets the targeted player (or you) choose when it resolves instead.</p>
          </div>
        </div>
      )}

      {isGm && token.token_type === "npc" && (
        <div className="space-y-2 border-t border-white/10 pt-3">
          <label className="flex items-center gap-2 text-xs text-[#f1e0c5]/70 cursor-pointer">
            <input
              data-testid="token-inspector-sight-cone-checkbox"
              type="checkbox"
              checked={!!token.sight_cone_enabled}
              onChange={(e) => patch({ sight_cone_enabled: e.target.checked })}
              className="accent-[#D90429]"
            />
            Directional Sight Cone (stealth checks for anyone who walks in front of it)
          </label>
          {token.sight_cone_enabled && (
            <div className="space-y-2 pl-1">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-[9px] uppercase text-[#f1e0c5]/30">Range (ft)</span>
                  <Input data-testid="sight-cone-range-input" type="number" value={draft.sight_range_ft ?? 60} onChange={(e) => setDraftField("sight_range_ft", e.target.value)} onBlur={() => commit("sight_range_ft", "sight_range_ft", toInt(0))} className="bg-black/30 border-white/10 h-7 text-xs" />
                </div>
                <div>
                  <span className="text-[9px] uppercase text-[#f1e0c5]/30">Width (deg)</span>
                  <Input data-testid="sight-cone-angle-input" type="number" value={draft.sight_angle_deg ?? 90} onChange={(e) => setDraftField("sight_angle_deg", e.target.value)} onBlur={() => commit("sight_angle_deg", "sight_angle_deg", toInt(0))} className="bg-black/30 border-white/10 h-7 text-xs" />
                </div>
                <div>
                  <span className="text-[9px] uppercase text-[#f1e0c5]/30">Facing (deg)</span>
                  <Input data-testid="sight-cone-direction-input" type="number" value={draft.sight_direction_deg ?? 0} onChange={(e) => setDraftField("sight_direction_deg", e.target.value)} onBlur={() => commit("sight_direction_deg", "sight_direction_deg", toInt(0))} className="bg-black/30 border-white/10 h-7 text-xs" />
                </div>
                <div>
                  <span className="text-[9px] uppercase text-[#f1e0c5]/30">Reset spotted</span>
                  <Button data-testid="sight-cone-reset-spotted-button" size="sm" variant="secondary" className="h-7 w-full text-[10px]" onClick={() => patch({ reset_spotted: true })}>Reset</Button>
                </div>
              </div>
              <p className="text-[9px] text-[#f1e0c5]/30">0° faces right, sweeping clockwise. DC scales between the two values below based on distance from this NPC — farthest is easiest, closest is hardest.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-[9px] uppercase text-[#f1e0c5]/30">DC at Far Edge</span>
                  <Input data-testid="sight-cone-dc-far-input" type="number" value={draft.sight_dc_far ?? 10} onChange={(e) => setDraftField("sight_dc_far", e.target.value)} onBlur={() => commit("sight_dc_far", "sight_dc_far", toInt(0))} className="bg-black/30 border-white/10 h-7 text-xs" />
                </div>
                <div>
                  <span className="text-[9px] uppercase text-[#f1e0c5]/30">DC Up Close</span>
                  <Input data-testid="sight-cone-dc-near-input" type="number" value={draft.sight_dc_near ?? 25} onChange={(e) => setDraftField("sight_dc_near", e.target.value)} onBlur={() => commit("sight_dc_near", "sight_dc_near", toInt(0))} className="bg-black/30 border-white/10 h-7 text-xs" />
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-[#f1e0c5]/70 cursor-pointer pt-1">
                <input
                  data-testid="sight-cone-pivot-checkbox"
                  type="checkbox"
                  checked={!!token.sight_pivot_enabled}
                  onChange={(e) => patch({ sight_pivot_enabled: e.target.checked })}
                  className="accent-[#D4AF37]"
                />
                Pivot — head turns as it watches
              </label>
              {token.sight_pivot_enabled && (
                <div className="grid grid-cols-3 gap-2 pl-1">
                  <Select value={token.sight_pivot_mode || "sweep"} onValueChange={(v) => patch({ sight_pivot_mode: v })}>
                    <SelectTrigger data-testid="sight-cone-pivot-mode-select" className="bg-black/30 border-white/10 h-7 text-xs col-span-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sweep">Sweep on a timer</SelectItem>
                      <SelectItem value="random">Scan randomly</SelectItem>
                    </SelectContent>
                  </Select>
                  <div>
                    <span className="text-[9px] uppercase text-[#f1e0c5]/30">Arc (deg)</span>
                    <Input data-testid="sight-cone-pivot-arc-input" type="number" value={draft.sight_pivot_arc_deg ?? 60} onChange={(e) => setDraftField("sight_pivot_arc_deg", e.target.value)} onBlur={() => commit("sight_pivot_arc_deg", "sight_pivot_arc_deg", toInt(0))} className="bg-black/30 border-white/10 h-7 text-xs" />
                  </div>
                  <div>
                    <span className="text-[9px] uppercase text-[#f1e0c5]/30">Period (s)</span>
                    <Input data-testid="sight-cone-pivot-period-input" type="number" value={draft.sight_pivot_period_sec ?? 6} onChange={(e) => setDraftField("sight_pivot_period_sec", e.target.value)} onBlur={() => commit("sight_pivot_period_sec", "sight_pivot_period_sec", toInt(1))} className="bg-black/30 border-white/10 h-7 text-xs" />
                  </div>
                </div>
              )}

              <p className="text-[9px] uppercase text-[#f1e0c5]/30 pt-1">Looking for (shows a RED warning cone to these players)</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto scrollbar-thin">
                {(members || []).filter((m) => m.role !== "gm").map((m) => {
                  const active = (token.looking_for_member_ids || []).includes(m.id);
                  return (
                    <button
                      key={m.id}
                      data-testid={`sight-cone-looking-for-${m.id}`}
                      onClick={() => {
                        const list = token.looking_for_member_ids || [];
                        patch({ looking_for_member_ids: active ? list.filter((id) => id !== m.id) : [...list, m.id] });
                      }}
                      className={`text-[10px] px-2 py-1 rounded-full border ${active ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/10 text-[#f1e0c5]/50"}`}
                    >
                      {m.display_name || "Player"}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {isGm && token.token_type === "npc" && !token.stat_block && (
        <div className="border-t border-white/10 pt-3">
          <Button data-testid="token-inspector-add-stat-block-button" size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs" onClick={addStatBlock}>
            <Swords className="w-3.5 h-3.5" /> Add Combat Stats
          </Button>
          <p className="text-[9px] text-[#f1e0c5]/30 mt-1">Give this NPC an AC, saves, and attacks so it can fight back — not just be attacked.</p>
        </div>
      )}

      {token.stat_block && (
        <div className="space-y-1.5 border-t border-white/10 pt-3" data-testid="token-inspector-stat-block">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Stat Block</p>
          {isGm ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[["sb_ac", "ac", "AC"], ["sb_speed", "speed", "Speed"], ["sb_perception", "perception", "Perc"]].map(([key, statKey, lbl]) => (
                  <div key={key}>
                    <span className="text-[9px] uppercase text-[#f1e0c5]/30">{lbl}</span>
                    <Input data-testid={`token-inspector-stat-${statKey}-input`} type="number" value={draft[key] ?? 0} onChange={(e) => setDraftField(key, e.target.value)} onBlur={() => commitStat(key, statKey)} className="bg-black/30 border-white/10 h-7 text-xs" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["sb_fort", "fort", "Fort"], ["sb_ref", "ref", "Ref"], ["sb_will", "will", "Will"]].map(([key, statKey, lbl]) => (
                  <div key={key}>
                    <span className="text-[9px] uppercase text-[#f1e0c5]/30">{lbl}</span>
                    <Input data-testid={`token-inspector-stat-${statKey}-input`} type="number" value={draft[key] ?? 0} onChange={(e) => setDraftField(key, e.target.value)} onBlur={() => commitStat(key, statKey)} className="bg-black/30 border-white/10 h-7 text-xs" />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-3 text-xs text-[#f1e0c5]/70">
                <span>AC {token.stat_block.ac}</span>
                <span>Speed {token.stat_block.speed}ft</span>
                <span>Perc {token.stat_block.perception >= 0 ? "+" : ""}{token.stat_block.perception}</span>
              </div>
              <div className="flex gap-3 text-xs text-[#f1e0c5]/70">
                <span>Fort {token.stat_block.fort >= 0 ? "+" : ""}{token.stat_block.fort}</span>
                <span>Ref {token.stat_block.ref >= 0 ? "+" : ""}{token.stat_block.ref}</span>
                <span>Will {token.stat_block.will >= 0 ? "+" : ""}{token.stat_block.will}</span>
              </div>
            </>
          )}
          {isGm && attackAnimKeys.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid="token-inspector-attack-anim-picker">
              <span className="text-[9px] uppercase text-[#f1e0c5]/30">Attack Animation:</span>
              {attackAnimKeys.map((k) => (
                <button
                  key={k}
                  data-testid={`token-inspector-attack-anim-${k}`}
                  onClick={() => setChosenAttackClip(k)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${effectiveAttackClip === k ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/10 text-[#f1e0c5]/50"}`}
                >
                  {ATTACK_CLIP_LABELS[k] || k}
                </button>
              ))}
            </div>
          )}
          {(token.stat_block.attacks || []).map((a, i) => {
            const targetForRow = pcTargets.find((t) => t.id === attackTargets[i]);
            const outOfRange = targetForRow && distanceToFt(targetForRow) > (a.range_ft || 5);
            return (
            <div key={i} className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#D4AF37]">{a.name} {a.bonus >= 0 ? "+" : ""}{a.bonus}, {a.damage} · {a.range_ft || 5}ft reach</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isGm && <button data-testid={`token-inspector-toggle-attack-sfx-${i}`} onClick={() => toggleAttackSfxOpen(i)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37]" title="Hit/Miss sound overrides"><Volume2 className="w-3.5 h-3.5" /></button>}
                  {isGm && <button data-testid={`token-inspector-remove-attack-${i}`} onClick={() => removeAttack(i)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
              {isGm && attackSfxOpen.has(i) && (
                <div className="space-y-1.5 pl-1 border-l border-white/10 ml-1">
                  <SoundPicker gameId={gameId} value={a.hit_sfx_id} onChange={(id) => setAttackSfx(i, "hit_sfx_id", id)} label="Hit Sound" testId={`npc-attack-hit-sound-${i}`} />
                  <SoundPicker gameId={gameId} value={a.miss_sfx_id} onChange={(id) => setAttackSfx(i, "miss_sfx_id", id)} label="Miss Sound" testId={`npc-attack-miss-sound-${i}`} />
                </div>
              )}
              {isGm && (
                <div className="flex items-center gap-1.5">
                  <Select value={attackTargets[i] || ""} onValueChange={(v) => setAttackTargets((s) => ({ ...s, [i]: v }))}>
                    <SelectTrigger data-testid={`npc-attack-target-select-${i}`} className="bg-black/30 border-white/10 h-7 text-[10px] flex-1"><SelectValue placeholder={pcTargets.length ? "Target…" : "No player tokens on map"} /></SelectTrigger>
                    <SelectContent>{pcTargets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button
                    data-testid={`npc-attack-roll-button-${i}`}
                    size="sm"
                    variant="secondary"
                    className="rounded-full h-7 text-[10px] shrink-0"
                    disabled={!attackTargets[i] || attackRolling[i] || outOfRange}
                    title={outOfRange ? `${targetForRow.name} is out of this attack's reach` : undefined}
                    onClick={() => rollNpcAttack(a, i)}
                  >
                    {outOfRange ? "Out of Range" : "Roll"}
                  </Button>
                </div>
              )}
              {attackResults[i] && (
                <p data-testid={`npc-attack-result-${i}`} className={`text-[10px] ${attackResults[i].outcome?.includes("hit") ? "text-[#4d7c0f]" : "text-[#D90429]"}`}>
                  {attackResults[i].outcome?.replace("_", " ")} (rolled {attackResults[i].roll_breakdown?.total})
                  {attackResults[i].damage?.effect_result?.final != null ? ` — ${attackResults[i].damage.effect_result.final} dmg` : ""}
                  {attackResults[i].damage?.effect_result?.conditions_applied?.length > 0 ? ` · ${attackResults[i].damage.effect_result.conditions_applied.join(", ")}` : ""}
                </p>
              )}
            </div>
            );
          })}
          {isGm && (
            <div className="flex items-center gap-1.5 pt-1">
              <Input data-testid="token-inspector-new-attack-name-input" value={newAttack.name} onChange={(e) => setNewAttack((a) => ({ ...a, name: e.target.value }))} placeholder="Name" className="bg-black/30 border-white/10 h-7 text-xs flex-1" />
              <Input data-testid="token-inspector-new-attack-bonus-input" type="number" value={newAttack.bonus} onChange={(e) => setNewAttack((a) => ({ ...a, bonus: e.target.value }))} placeholder="+bonus" className="bg-black/30 border-white/10 h-7 text-xs w-16" />
              <Input data-testid="token-inspector-new-attack-damage-input" value={newAttack.damage} onChange={(e) => setNewAttack((a) => ({ ...a, damage: e.target.value }))} placeholder="1d6+3" className="bg-black/30 border-white/10 h-7 text-xs w-20" />
              <Input data-testid="token-inspector-new-attack-range-input" type="number" value={newAttack.range_ft} onChange={(e) => setNewAttack((a) => ({ ...a, range_ft: e.target.value }))} placeholder="5ft" title="Reach/range in feet — melee weapons should stay 5-10ft" className="bg-black/30 border-white/10 h-7 text-xs w-16" />
              <Button data-testid="token-inspector-add-attack-button" size="sm" variant="secondary" className="rounded-full h-7 text-[10px] shrink-0" onClick={addAttack}>Add</Button>
            </div>
          )}
          {token.stat_block.abilities && <p className="text-xs text-[#f1e0c5]/50 font-body">{token.stat_block.abilities}</p>}
        </div>
      )}

      {isGm && hasGmPass && (
        <div className="space-y-2 border-t border-white/10 pt-3" data-testid="token-inspector-animation-section">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Token Animation</p>
          {token.is_animated ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#4d7c0f]" data-testid="token-inspector-animation-status">Animated — {Object.keys(token.animation_clips || {}).length} clip(s)</span>
              <Button data-testid="token-inspector-clear-animation" size="sm" variant="secondary" className="rounded-full text-xs h-7" onClick={clearTokenAnimation}>Remove</Button>
            </div>
          ) : (
            <p className="text-[10px] text-[#f1e0c5]/30 italic">No animation applied — uses the static image above.</p>
          )}
          <div className="flex gap-2">
            {myAnimations.length > 0 && (
              <Select onValueChange={applyExistingAnimation}>
                <SelectTrigger data-testid="token-inspector-apply-animation-select" className="bg-black/30 border-white/10 h-8 text-xs flex-1"><SelectValue placeholder="Apply from library…" /></SelectTrigger>
                <SelectContent>{myAnimations.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button data-testid="token-inspector-open-animation-maker" size="sm" variant="secondary" className="rounded-full text-xs gap-1.5 shrink-0" onClick={() => setAnimMakerOpen(true)}>
              <Wand2 className="w-3.5 h-3.5" /> {token.is_animated ? "New" : "Create"}
            </Button>
          </div>
          <TokenAnimationMakerModal
            open={animMakerOpen}
            onOpenChange={(v) => { setAnimMakerOpen(v); if (!v) refreshMyAnimations(); }}
            gameId={gameId}
            applyToTokenId={token.id}
          />
        </div>
      )}

      {token.restrained && (
        <div className="flex items-center justify-between bg-black/30 border border-[#D4AF37]/30 rounded-lg px-3 py-2" data-testid="token-inspector-restrained-status">
          <span className="text-xs text-[#D4AF37]">Restrained — Escape DC {token.restrain_dc}</span>
          {(isGm || token.created_by === myMemberId) && (
            <Button data-testid="attempt-break-free-button" size="sm" variant="secondary" className="rounded-full text-xs h-7" onClick={() => api.post(`/games/${gameId}/tokens/${token.id}/attempt-break-free`)}>
              Attempt to Break Free
            </Button>
          )}
        </div>
      )}

      {isGm && (
        <div>
          <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Conditions</p>
          <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto scrollbar-thin">
            {conditions.map((c) => {
              const active = tokenConditions.some((tc) => tc.slug === c.slug);
              return (
                <button
                  key={c.slug}
                  data-testid={`condition-toggle-${c.slug}`}
                  onClick={() => toggleCondition(c.slug)}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors duration-200 ${
                    active ? "text-white" : "text-[#f1e0c5]/50 border-white/10"
                  }`}
                  style={active ? { backgroundColor: c.badge_color, borderColor: c.badge_color } : {}}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button data-testid="token-inspector-swap-image" size="sm" variant="secondary" className="rounded-full gap-1.5 flex-1" onClick={() => setSwapOpen(true)}>
          <ImagePlus className="w-3.5 h-3.5" /> Swap Image
        </Button>
        {isGm && (
          <Button data-testid="token-inspector-duplicate" size="sm" variant="secondary" className="rounded-full gap-1.5" onClick={duplicateToken} title="Duplicate this token as an independent copy">
            <Copy className="w-3.5 h-3.5" />
          </Button>
        )}
        <Button data-testid="token-inspector-delete" size="sm" className="rounded-full gap-1.5 bg-[#D90429] hover:bg-[#b8031f]" onClick={removeToken}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      <TokenUploadCropModal
        open={swapOpen}
        onOpenChange={setSwapOpen}
        gameId={gameId}
        onUploaded={({ url }) => patch({ image_url: url })}
      />
    </div>
  );
}
